import { Dialog } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import Papa from 'papaparse';
import { Subject, takeUntil } from 'rxjs';
import { PalantirConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import { PalantirConfirmDialogResult } from '../confirm-dialog/confirm-dialog.type';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirToastService } from '../toast/toast.service';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import type { FxDynamicTabularDialogData } from './components/dynamic-tabular-dialog/dynamic-tabular-dialog.component';
import { FxDynamicTabularTableCellComponent } from './components/dynamic-tabular-table-cell/dynamic-tabular-table-cell.component';
import { FxDynamicTabularTableEmptyStateComponent } from './components/dynamic-tabular-table-empty-state/dynamic-tabular-table-empty-state.component';
import { FxDynamicTabularTableHeaderCellComponent } from './components/dynamic-tabular-table-header-cell/dynamic-tabular-table-header-cell.component';
import { FxDynamicTabularTableRowActionsComponent } from './components/dynamic-tabular-table-row-actions/dynamic-tabular-table-row-actions.component';
import {
  FX_DYNAMIC_TABULAR_CHAR_LIMIT_PER_CELL,
  FX_DYNAMIC_TABULAR_COLUMN_LIMIT,
  FX_DYNAMIC_TABULAR_ROW_LIMIT,
} from './dynamic-tabular.constants';
import { FxDynamicTabularComponentConfig } from './dynamic-tabular.type';

@Component({
  selector: 'fx-dynamic-tabular',
  standalone: true,
  imports: [
    CommonModule,
    PalantirIconComponent,
    PalantirTooltipModule,
    FxDynamicTabularTableCellComponent,
    FxDynamicTabularTableEmptyStateComponent,
    FxDynamicTabularTableHeaderCellComponent,
    FxDynamicTabularTableRowActionsComponent,
  ],
  templateUrl: './dynamic-tabular.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FxDynamicTabularComponent {
  public readonly ROW_LIMIT = FX_DYNAMIC_TABULAR_ROW_LIMIT;

  public readonly COLUMN_LIMIT = FX_DYNAMIC_TABULAR_COLUMN_LIMIT;

  public readonly control = input.required<FormControl<string[][]>>();

  public readonly readonly = input<boolean>(false);

  public readonly schema = input<FxDynamicTabularComponentConfig | null>(null);

  public readonly hideFullScreen = input(false);

  private confirmationDialog = inject(PalantirConfirmDialogService);

  private destroyRef = inject(DestroyRef);

  private dialogService = inject(Dialog);

  private toastService = inject(PalantirToastService);

  public readonly editingCell = signal<{ col: number; row: number } | null>(null);

  public readonly data: WritableSignal<string[][]> = signal([]);

  private readonly computeNumRows = () => this.data().length;

  public readonly numRows = computed(this.computeNumRows.bind(this));

  private readonly computeNumCols = () => (this.data()[0] ?? []).length;

  public readonly numCols = computed(this.computeNumCols.bind(this));

  private readonly computeCanAddRow = () => {
    const config = this.schema();

    const maxRows = config?.fixedHeaders?.length ? this.numRows() - 1 : this.numRows();

    return !config?.maxRows || maxRows < config.maxRows;
  };

  public readonly canAddRow = computed(this.computeCanAddRow.bind(this));

  private readonly computeCanRemoveRow = () => {
    const config = this.schema();
    return !config?.minRows || this.numRows() > config.minRows;
  };

  public readonly canRemoveRow = computed(this.computeCanRemoveRow.bind(this));

  private readonly computeCanAddColumn = () => {
    const config = this.schema();
    if (!config) return true;
    if (config.fixedHeaders?.length || !config.maxCols) return false;
    return this.numCols() < config.maxCols;
  };

  public readonly canAddColumn = computed(this.computeCanAddColumn.bind(this));

  private readonly computeCanRemoveColumn = () => {
    const config = this.schema();
    if (!config) return true;
    if (config.fixedHeaders?.length || !config.minCols) return false;
    return this.numCols() > config.minCols;
  };

  public readonly canRemoveColumn = computed(this.computeCanRemoveColumn.bind(this));

  private readonly computeCanEditFirstRow = () => {
    const config = this.schema();
    return !config?.fixedHeaders?.length;
  };

  public readonly canEditFirstRow = computed(this.computeCanEditFirstRow.bind(this));

  private readonly computeCanPaste = () => {
    const config = this.schema();
    return !config || config.allowPaste;
  };

  public readonly canPaste = computed(this.computeCanPaste.bind(this));

  public prefilled = false;

  private unsubscribeOldEvents$ = new Subject<void>();

  public constructor() {
    effect(() => {
      this.unsubscribeOldEvents$.next();
      this.control()
        .valueChanges.pipe(
          takeUntilDestroyed(this.destroyRef),
          takeUntil(this.unsubscribeOldEvents$),
        )
        .subscribe((value) => {
          if (!this.prefilled && value && !value.length) {
            const config = this.schema();
            let defaultData = this.createDefaultData(config);
            defaultData = this.ensureFirstRowAreHeaders(config, defaultData);

            this.control().setValue(defaultData, { emitEvent: false });
            this.data.set(defaultData);

            this.prefilled = true;
            return;
          } else if (!this.prefilled && value) {
            const config = this.schema();
            let startedValue = value;
            if (config?.fixedHeaders?.length) {
              startedValue = this.ensureFirstRowAreHeaders(config, value);
            }

            this.data.set(startedValue);
            this.prefilled = true;
            return;
          }

          this.prefilled = true;

          this.data.set(value || []);
        });

      let value = this.control().value ?? [];
      const config = this.schema();
      value = this.ensureFirstRowAreHeaders(config, value);

      this.control().setValue(value, { emitEvent: false });
      this.data.set(value);
    });
  }

  public isEditing(rowIndex: number, colIndex: number): boolean {
    if (this.readonly() || this.control().disabled) return false;
    const editing = this.editingCell();
    return editing?.row === rowIndex && editing?.col === colIndex;
  }

  public editCell(rowIndex: number, colIndex: number): void {
    if (this.readonly() || (!this.canEditFirstRow() && rowIndex === 0) || this.control().disabled)
      return;

    this.editingCell.set({ row: rowIndex, col: colIndex });
    setTimeout(() => {
      const textarea = document.querySelector(
        `tbody tr:nth-child(${rowIndex + 1}) td:nth-child(${colIndex + 2}) textarea`,
      ) as HTMLTextAreaElement;
      textarea?.focus();
      textarea?.select();
    }, 0);
  }

  public updateCell(event: Event, rowIndex: number, colIndex: number): void {
    if (this.readonly()) return;
    const value = (event.target as HTMLInputElement).value;
    const currentData = [...(this.control().value || [])];
    if (currentData[rowIndex]) {
      currentData[rowIndex] = [...currentData[rowIndex]];
      currentData[rowIndex][colIndex] = value;
      this.control().setValue(currentData);
    }

    this.editingCell.set(null);
  }

  public updateCellValue(value: string, rowIndex: number, colIndex: number): void {
    if (this.readonly()) return;
    const currentData = [...(this.control().value || [])];
    if (currentData[rowIndex]) {
      currentData[rowIndex] = [...currentData[rowIndex]];
      currentData[rowIndex][colIndex] = value;
      this.control().setValue(currentData);
    }

    this.editingCell.set(null);
  }

  public handleCellKeydown(event: KeyboardEvent, rowIndex: number, colIndex: number): void {
    if (this.readonly()) return;

    const isEditing = this.isEditing(rowIndex, colIndex);

    if (isEditing) {
      switch (event.key) {
        case 'Enter':
          if (!event.shiftKey) {
            event.preventDefault();
            (event.target as HTMLTextAreaElement).blur();
            this.navigateToCell(rowIndex + 1, colIndex);
          }

          break;
        case 'Tab':
          event.preventDefault();
          (event.target as HTMLTextAreaElement).blur();
          this.navigateToCell(rowIndex, colIndex + (event.shiftKey ? -1 : 1));
          break;
        case 'Escape':
          (event.target as HTMLTextAreaElement).blur();
          this.editingCell.set(null);
          break;
      }

      return;
    }

    switch (event.key) {
      case 'Enter':
      case 'F2':
      case ' ':
        event.preventDefault();
        this.editCell(rowIndex, colIndex);
        break;
      case 'Tab':
        event.preventDefault();
        this.navigateToCell(rowIndex, colIndex + (event.shiftKey ? -1 : 1));
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.navigateToCell(rowIndex - 1, colIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.navigateToCell(rowIndex + 1, colIndex);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.navigateToCell(rowIndex, colIndex - 1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.navigateToCell(rowIndex, colIndex + 1);
        break;
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        this.clearCell(rowIndex, colIndex);
        break;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          this.editCell(rowIndex, colIndex);
          setTimeout(() => {
            const textarea = document.querySelector(
              `tbody tr:nth-child(${rowIndex + 1}) td:nth-child(${colIndex + 2}) textarea`,
            ) as HTMLTextAreaElement;
            if (textarea) {
              textarea.value = event.key;
              textarea.setSelectionRange(1, 1);
            }
          }, 0);
        }

        break;
    }
  }

  private navigateToCell(rowIndex: number, colIndex: number): void {
    const maxRow = this.numRows() - 1;
    const maxCol = this.numCols() - 1;

    if (rowIndex < 0 || colIndex < 0 || rowIndex > maxRow || colIndex > maxCol) {
      return;
    }

    this.focusCell(rowIndex, colIndex);
  }

  private focusCell(rowIndex: number, colIndex: number): void {
    setTimeout(() => {
      const cell = document.querySelector(
        `tbody tr:nth-child(${rowIndex + 1}) td:nth-child(${colIndex + 2}) div`,
      ) as HTMLElement;
      cell?.focus();
    }, 0);
  }

  private clearCell(rowIndex: number, colIndex: number): void {
    const currentData = [...(this.control().value || [])];
    if (currentData[rowIndex]) {
      currentData[rowIndex] = [...currentData[rowIndex]];
      currentData[rowIndex][colIndex] = '';
      this.control().setValue(currentData);
    }
  }

  public addRow(): void {
    if (this.readonly() || !this.canAddRow()) return;

    const currentData = this.control().value ? [...this.control().value!] : [];
    const newRow = Array.from<string>({
      length: this.numCols(),
    }).fill('');

    this.control().setValue([...currentData, newRow], {
      emitEvent: false,
    });

    this.data.set([...currentData, newRow]);
  }

  public addColumn(): void {
    if (this.readonly() || !this.canAddColumn()) return;

    let currentData = this.control().value ? [...this.control().value!] : [];
    if (currentData.length === 0) {
      currentData = [['']];
    } else {
      currentData = currentData.map((row) => [...row, '']);
    }

    this.control().setValue(currentData, {
      emitEvent: false,
    });

    this.data.set(currentData);
  }

  public removeRow(rowIndex: number): void {
    if (this.readonly() || !this.canRemoveRow()) return;

    const rowToRemovedHasValues = (this.control().value?.[rowIndex] ?? []).some(
      (cell) => cell && cell.trim() !== '',
    );

    const currentData = [...(this.control().value || [])];
    currentData.splice(rowIndex, 1);
    this.control().setValue(currentData, {
      emitEvent: rowToRemovedHasValues,
    });

    if (!rowToRemovedHasValues) {
      this.data.set(currentData);
    }
  }

  public removeColumn(colIndex: number): void {
    if (this.readonly() || !this.canRemoveColumn()) return;
    const currentData = [...(this.control().value || [])];

    const columnToRemovedHasValues = currentData.some((row) => {
      const cell = row[colIndex];
      return cell && cell.trim() !== '';
    });

    const newData = currentData.map((row) => {
      const newRow = [...row];
      newRow.splice(colIndex, 1);
      return newRow;
    });

    this.control().setValue(newData, {
      emitEvent: columnToRemovedHasValues,
    });

    if (!columnToRemovedHasValues) {
      this.data.set(newData);
    }
  }

  public async handlePaste(event: ClipboardEvent): Promise<void> {
    if (this.readonly() || !this.canPaste()) return;
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const parsedData = this.parseCsv(pastedText);

    if (parsedData.length === 0) return;

    const isDataExceedingLimits =
      parsedData.length > this.ROW_LIMIT || (parsedData[0]?.length || 0) > this.COLUMN_LIMIT;

    const currentData = this.control().value;
    if (currentData && currentData.length > 0) {
      const confirmed = this.confirmationDialog.openDialog({
        successText: 'Confirmar',
        text: 'Sobrescrever os dados existentes?',
        title: 'Confirmar ação',
        cancelText: 'Cancelar',
      });

      const result = (await confirmed.afterClosed()) as PalantirConfirmDialogResult;

      if (result?.role !== 'success') {
        return;
      }
    }

    if (isDataExceedingLimits) {
      this.toastService.showWarning(
        `Os dados colados excedem o limite. Apenas os primeiros ${this.ROW_LIMIT} linhas e ${this.COLUMN_LIMIT} colunas serão considerados.`,
      );
    }

    let safeData = parsedData
      .slice(0, this.ROW_LIMIT)
      .map((row) =>
        row
          .slice(0, this.COLUMN_LIMIT)
          .map((cell) => cell.slice(0, FX_DYNAMIC_TABULAR_CHAR_LIMIT_PER_CELL).trim()),
      );

    if (this.schema()) {
      const config = this.schema()!;
      if (config.fixedHeaders?.length) {
        safeData = this.ensureFirstRowAreHeaders(config, safeData);
      }
    }

    this.control().setValue(safeData);

    // Focus first cell after paste
    setTimeout(() => this.focusCell(0, 0), 100);
  }

  private parseCsv(text: string): string[][] {
    if (!text) return [];
    try {
      return Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
      }).data as string[][];
    } catch {
      return [];
    }
  }

  public async openInFullscreen(): Promise<void> {
    const component =
      await import('./components/dynamic-tabular-dialog/dynamic-tabular-dialog.component');
    this.dialogService.open(component.FxDynamicTabularDialogComponent, {
      data: {
        control: this.control(),
        readonly: this.readonly(),
        schema: this.schema(),
      } satisfies FxDynamicTabularDialogData,
      width: '100%',
      height: '100%',
      ariaLabelledBy: 'automation-center-dialog',
      maxHeight: '100%',
      maxWidth: '100%',
      disableClose: true,
    });
  }

  public blurTarget(event: Event): void {
    (event.target as HTMLTextAreaElement).blur();
  }

  private createDefaultData(config: FxDynamicTabularComponentConfig | null): string[][] {
    if (!config) {
      return [
        ['', '', ''],
        ['', '', ''],
        ['', '', ''],
      ];
    }

    const rows = Math.max(config.minRows ?? 1, 1);
    const cols = config.fixedHeaders?.length ?? Math.max(config.minCols ?? 1, 1);
    const data: string[][] = [];

    // Add header row if fixed headers exist
    if (config.fixedHeaders?.length) {
      data.push([...config.fixedHeaders]);
    }

    // Add data rows
    for (let idx = config.fixedHeaders?.length ? 1 : 0; idx < rows; idx++) {
      data.push(Array.from<string>({ length: cols }).fill(''));
    }

    return data;
  }

  private ensureFirstRowAreHeaders(
    config: FxDynamicTabularComponentConfig | null,
    data: string[][],
  ): string[][] {
    if (!config?.fixedHeaders?.length) {
      return data;
    }

    if (data.length === 0) {
      return [config.fixedHeaders];
    }

    const firstRow = data[0];
    let headersMatch = true;
    for (let idx = 0; idx < config.fixedHeaders?.length; idx++) {
      if (firstRow[idx] !== config.fixedHeaders[idx]) {
        headersMatch = false;
        break;
      }
    }

    if (headersMatch) {
      return data;
    }

    const newData = [...data];
    newData.unshift([...config.fixedHeaders]);
    return newData;
  }

  public getColumnHeader(colIndex: number): string | null {
    let colIndexClone = colIndex;

    let header = '';
    while (colIndexClone >= 0) {
      header = String.fromCodePoint((colIndexClone % 26) + 65) + header;
      colIndexClone = Math.floor(colIndexClone / 26) - 1;
    }

    return header;
  }

  public getColumnType(colIndex: number): 'boolean' | 'date' | 'number' | 'text' {
    const config = this.schema();
    if (!config) return 'text';

    const header = config.fixedHeaders?.[colIndex];
    if (header && config.columnTypes?.[header]) {
      return config.columnTypes[header];
    }

    return 'text';
  }
}
